import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight, Mail, Phone, User, MapPin, Calendar, Clock } from 'lucide-react';
import { useLocation } from 'wouter';
import { apiBaseUrl } from '@/lib/apiBaseUrl';
import {
  SERVICE_PRICING_OPTIONS,
  SERVICE_KEYS,
  SERVICE_PARAM_ALIASES,
  type ServiceKey,
  getServiceLabel,
} from '@/lib/services';
import { getAdsContext } from '@/lib/trafficTracking';

const CalendarModal = lazy(() => import('@/components/CalendarModal'));
const TimeModal = lazy(() => import('@/components/TimeModal'));

interface AppointmentFormProps {
  isOpen?: boolean;
  onClose?: () => void;
  prefilledDateTime?: { date: string; time: string } | null;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  service?: string;
  servicePricing?: string;
  city?: string;
  date?: string;
  time?: string;
  captcha?: string;
}

interface FormTouched {
  fullName?: boolean;
  email?: boolean;
  phone?: boolean;
  service?: boolean;
  servicePricing?: boolean;
  city?: boolean;
  date?: boolean;
  time?: boolean;
  captcha?: boolean;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  service: string;
  city: string;
  date: string;
  time: string;
}

interface AttributionData {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  gclid: string;
  fbclid: string;
}

type TrackingPayload = Record<string, string | number | boolean | undefined>;

const CITY_PARAM_ALIASES: Record<string, string> = {
  casablanca: 'casablanca',
  rabat: 'rabat',
};

const EMPTY_ATTRIBUTION: AttributionData = {
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmContent: '',
  utmTerm: '',
  gclid: '',
  fbclid: '',
};

function normalizeLookupKey(value: string) {
  return value.trim().toLowerCase().replace(/[_\s]+/g, '-');
}

function normalizeServiceParam(rawValue: string | null) {
  if (!rawValue) {
    return '';
  }

  return SERVICE_PARAM_ALIASES[normalizeLookupKey(rawValue)] || '';
}

function normalizeCityParam(rawValue: string | null) {
  if (!rawValue) {
    return '';
  }

  return CITY_PARAM_ALIASES[normalizeLookupKey(rawValue)] || '';
}

function normalizeDateParam(rawValue: string | null) {
  if (!rawValue) {
    return '';
  }

  const value = rawValue.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

function normalizeTimeParam(rawValue: string | null) {
  if (!rawValue) {
    return '';
  }

  const value = rawValue.trim();
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value) ? value : '';
}

function readParamValue(params: URLSearchParams, key: string, maxLength = 255) {
  const rawValue = params.get(key);
  if (!rawValue) {
    return '';
  }

  return rawValue.trim().slice(0, maxLength);
}

function readAttributionParams(params: URLSearchParams): AttributionData {
  return {
    utmSource: readParamValue(params, 'utm_source'),
    utmMedium: readParamValue(params, 'utm_medium'),
    utmCampaign: readParamValue(params, 'utm_campaign'),
    utmContent: readParamValue(params, 'utm_content'),
    utmTerm: readParamValue(params, 'utm_term'),
    gclid: readParamValue(params, 'gclid'),
    fbclid: readParamValue(params, 'fbclid'),
  };
}

const reCaptchaSiteKey = String(import.meta.env.VITE_RECAPTCHA_SITE_KEY || '').trim();

const translations = {
  fr: {
    title: 'Réservez votre rendez-vous',
    subtitle: 'Choisissez votre soin, votre centre et votre créneau, puis renseignez vos coordonnées.',
    fullName: 'Nom Complet',
    email: 'Adresse Email',
    phone: 'Numéro de Téléphone',
    service: 'Choisissez votre soin',
    city: 'Ville',
    date: 'Date',
    time: 'Heure',
    submit: 'Réserver ma consultation',
    submitting: 'Confirmation...',
    submitWomen: 'Réserver ma consultation',
    submittingWomen: 'Envoi de votre demande...',
    continue: 'Renseigner mes coordonnées',
    back: 'Retour',
    womenQuickReply: 'Sans engagement. Reponse rapide.',
    successTitle: 'Merci ! 🎉',
    successSubtitle: 'Votre rendez-vous a été confirmé avec succès',
    successMessage: 'Nous avons bien reçu votre réservation et vous enverrons une confirmation sous peu.',
    bookingDetails: 'Détails de votre réservation :',
    errorMessage: 'Impossible de confirmer le rendez-vous. Veuillez réessayer.',
    openCalendar: 'Ouvrir le calendrier',
    openTimes: 'Choisir une heure',
    cities: {
      casablanca: 'Casablanca',
      rabat: 'Rabat',
    },
  },
  ar: {
    title: 'احجز جلستك',
    subtitle: 'اختر علاجك ومركزك وموعدك، ثم أدخل معلوماتك الشخصية.',
    fullName: 'الاسم الكامل',
    email: 'عنوان البريد الإلكتروني',
    phone: 'رقم الهاتف',
    service: 'اختر علاجك',
    city: 'المدينة',
    date: 'التاريخ',
    time: 'الوقت',
    submit: 'تأكيد الموعد',
    submitting: 'جاري التأكيد...',
    submitWomen: 'احصلي على تشخيص مجاني',
    submittingWomen: 'جاري ارسال طلبك...',
    continue: 'إدخال معلوماتي',
    back: 'رجوع',
    womenQuickReply: 'بدون التزام. رد سريع.',
    successTitle: 'شكراً ! 🎉',
    successSubtitle: 'تم تأكيد موعدك بنجاح',
    successMessage: 'لقد تلقينا حجزك وسنرسل لك تأكيداً قريباً.',
    bookingDetails: 'تفاصيل حجزك :',
    errorMessage: 'تعذر تأكيد الموعد. يرجى المحاولة مرة أخرى.',
    openCalendar: 'فتح التقويم',
    openTimes: 'اختر وقتاً',
    cities: {
      casablanca: 'الدار البيضاء',
      rabat: 'الرباط',
    },
  },
  en: {
    title: 'Book your session',
    subtitle: 'Choose your treatment, location, and time, then enter your contact details.',
    fullName: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    service: 'Choose your treatment',
    city: 'City',
    date: 'Date',
    time: 'Time',
    submit: 'Confirm Appointment',
    submitting: 'Confirming...',
    submitWomen: 'Get My Free Consultation',
    submittingWomen: 'Sending your request...',
    continue: 'Enter my details',
    back: 'Back',
    womenQuickReply: 'No commitment. Fast callback.',
    successTitle: 'Thank You! 🎉',
    successSubtitle: 'Your appointment has been successfully confirmed',
    successMessage: 'We have received your booking and will send you a confirmation shortly.',
    bookingDetails: 'Your booking details:',
    errorMessage: 'Unable to confirm appointment. Please try again.',
    openCalendar: 'Open Calendar',
    openTimes: 'Choose a time',
    cities: {
      casablanca: 'Casablanca',
      rabat: 'Rabat',
    },
  },
};

export function AppointmentForm({ isOpen = true, onClose, prefilledDateTime }: AppointmentFormProps) {
  const { language } = useLanguage();
  const t = language === 'fr' ? translations.fr : language === 'ar' ? translations.ar : translations.en;
  const isRTL = language === 'ar';
  const isWomenSite = false;
  const [, setLocation] = useLocation();
  const formRef = useRef<HTMLFormElement | null>(null);

  const urlContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        prefill: {
          service: '',
          city: '',
          date: '',
          time: '',
        },
        attribution: EMPTY_ATTRIBUTION,
      };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      prefill: {
        service: normalizeServiceParam(params.get('service')),
        city: normalizeCityParam(params.get('city')),
        date: normalizeDateParam(params.get('date')),
        time: normalizeTimeParam(params.get('time')),
      },
      attribution: readAttributionParams(params),
    };
  }, []);

  const attribution = urlContext.attribution;
  const hasTrackedFormStart = useRef(false);
  
  const [formData, setFormData] = useState<FormData>(() => ({
    fullName: '',
    email: '',
    phone: '',
    service: urlContext.prefill.service,
    city: urlContext.prefill.city,
    date: prefilledDateTime?.date || urlContext.prefill.date,
    time: prefilledDateTime?.time || urlContext.prefill.time,
  }));
  const [servicePricing, setServicePricing] = useState<string[]>(() => {
    const initialPricingOptions = SERVICE_PRICING_OPTIONS[urlContext.prefill.service as ServiceKey] || [];
    return initialPricingOptions[0] ? [initialPricingOptions[0].value] : [];
  });
  const currentPricingOptions = SERVICE_PRICING_OPTIONS[formData.service as ServiceKey] || [];

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [formStep, setFormStep] = useState<1 | 2>(1);

  const buildTrackingPayload = (extraPayload: TrackingPayload = {}): TrackingPayload => ({
    site_target: 'men',
    utm_source: attribution.utmSource || undefined,
    utm_medium: attribution.utmMedium || undefined,
    utm_campaign: attribution.utmCampaign || undefined,
    utm_content: attribution.utmContent || undefined,
    utm_term: attribution.utmTerm || undefined,
    gclid: attribution.gclid || undefined,
    fbclid: attribution.fbclid || undefined,
    ...extraPayload,
  });

  const trackConversionEvent = (eventName: string, payload: TrackingPayload = {}) => {
    if (typeof window === 'undefined') {
      return;
    }

    const trackedPayload = buildTrackingPayload(payload);
    const win = window as Window & {
      dataLayer?: Array<Record<string, unknown>>;
      umami?: { track?: (name: string, payload?: Record<string, unknown>) => void };
    };

    if (Array.isArray(win.dataLayer)) {
      win.dataLayer.push({
        event: eventName,
        ...trackedPayload,
      });
    }

    if (typeof win.umami?.track === 'function') {
      win.umami.track(eventName, trackedPayload);
    }
  };

  const markFormStarted = () => {
    if (hasTrackedFormStart.current) {
      return;
    }

    hasTrackedFormStart.current = true;
    trackConversionEvent(
      'form_start',
      {
        form_variant: isWomenSite ? 'women_minimal' : 'standard',
      }
    );
  };

  const getFieldError = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    switch (field) {
      case 'fullName':
        return String(value).trim() ? undefined : 'Required';
      case 'email': {
        const emailValue = String(value).trim();
        if (!emailValue) {
          return undefined;
        }

        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue) ? undefined : 'Invalid email';
      }
      case 'phone':
        {
          const phoneValue = String(value).trim();
          if (!phoneValue) {
            return 'Required';
          }

          return /^\d+$/.test(phoneValue) ? undefined : 'Invalid phone';
        }
      case 'service':
      case 'city':
        return String(value).trim() ? undefined : 'Required';
      case 'date':
      case 'time':
        if (isWomenSite) {
          return undefined;
        }

        return String(value).trim() ? undefined : 'Required';
      default:
        return undefined;
    }
  };

  const getPricingError = () => {
    return currentPricingOptions.length > 0 && (servicePricing.length !== 1 || !currentPricingOptions.some((option) => option.value === servicePricing[0]))
      ? 'Required'
      : undefined;
  };

  const syncBrowserFilledFields = () => {
    const formElement = formRef.current;
    if (!formElement) {
      return;
    }

    const browserFormData = new window.FormData(formElement);
    const syncedFields: Partial<FormData> = {};

    (['fullName', 'email', 'phone'] as Array<keyof FormData>).forEach((field) => {
      const rawDomValue = String(browserFormData.get(field) || '');
      const domValue = field === 'phone' ? rawDomValue.replace(/\D/g, '') : rawDomValue;
      if (domValue && domValue !== formData[field]) {
        syncedFields[field] = domValue;
      }
    });

    if (Object.keys(syncedFields).length === 0) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      ...syncedFields,
    }));

    setErrors((previous) => {
      const nextErrors = { ...previous };

      (Object.keys(syncedFields) as Array<keyof FormData>).forEach((field) => {
        nextErrors[field] = getFieldError(field, syncedFields[field] as FormData[keyof FormData]);
      });

      return nextErrors;
    });
  };

  const updateFormField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    markFormStarted();
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
    setErrors((previous) => {
      const nextError = getFieldError(field, value);

      if (!previous[field] && !nextError) {
        return previous;
      }

      return {
        ...previous,
        [field]: nextError,
      };
    });
  };

  useEffect(() => {
    trackConversionEvent(
      'form_view',
      {
        form_variant: isWomenSite ? 'women_minimal' : 'standard',
      }
    );
    // Track only once when the form component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const syncHandle = window.setTimeout(() => {
      syncBrowserFilledFields();
    }, 0);

    return () => window.clearTimeout(syncHandle);
    // Intentionally run once after mount to catch browser autofill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toLocalDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toLocalTimeString = (date: Date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const getWomenAutoSlot = (attempt: number) => {
    const candidate = new Date();
    // Spread retries across different minutes to avoid slot collisions.
    candidate.setSeconds(0, 0);
    candidate.setMinutes(candidate.getMinutes() + 5 + attempt * 3);

    // Backend rejects Sunday bookings.
    while (candidate.getDay() === 0) {
      candidate.setDate(candidate.getDate() + 1);
    }

    return {
      date: toLocalDateString(candidate),
      time: toLocalTimeString(candidate),
    };
  };

  const loadRecaptchaScript = () => {
    if (!reCaptchaSiteKey || typeof window === 'undefined') {
      return Promise.resolve();
    }

    const currentWindow = window as Window & {
      grecaptcha?: unknown;
    };

    if (currentWindow.grecaptcha) {
      return Promise.resolve();
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/api.js"]');
    if (existingScript) {
      return new Promise<void>((resolve) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => resolve(), { once: true });
      });
    }

    return new Promise<void>((resolve) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${reCaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(script);
    });
  };

  const getRecaptchaToken = async () => {
    if (!reCaptchaSiteKey || typeof window === 'undefined') {
      return '';
    }

    await loadRecaptchaScript();

    const grecaptcha = (window as Window & {
      grecaptcha?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options?: { action: string }) => Promise<string>;
      };
    }).grecaptcha;

    if (!grecaptcha) {
      return '';
    }

    return new Promise<string>((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(reCaptchaSiteKey, { action: 'submit_appointment' })
          .then((token) => resolve(token || ''))
          .catch(() => resolve(''));
      });
    });
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    (Object.keys(formData) as Array<keyof FormData>).forEach((field) => {
      const error = getFieldError(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    const pricingError = getPricingError();
    if (pricingError) {
      newErrors.servicePricing = pricingError;
    }

    // Mark all fields as touched so error messages become visible
    const allTouched: FormTouched = {};
    (Object.keys(formData) as Array<keyof FormData>).forEach((field) => {
      allTouched[field] = true;
    });
    allTouched.servicePricing = true;
    setTouched(allTouched);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFirstStep = () => {
    const stepFields: Array<keyof FormData> = isWomenSite
      ? ['service', 'city']
      : ['service', 'city', 'date', 'time'];
    const stepErrors: FormErrors = {};
    const stepTouched: FormTouched = {};

    stepFields.forEach((field) => {
      const error = getFieldError(field, formData[field]);
      if (error) {
        stepErrors[field] = error;
      }
      stepTouched[field] = true;
    });

    const pricingError = getPricingError();
    if (pricingError) {
      stepErrors.servicePricing = pricingError;
    }
    stepTouched.servicePricing = true;

    setErrors((previous) => ({ ...previous, ...stepErrors }));
    setTouched((previous) => ({ ...previous, ...stepTouched }));

    if (Object.keys(stepErrors).length === 0) {
      setFormStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    syncBrowserFilledFields();
    setApiError('');
    markFormStarted();
    trackConversionEvent('form_submit', {
      service: formData.service || 'unselected',
      city: formData.city || 'unselected',
      form_variant: isWomenSite ? 'women_minimal' : 'standard',
    });

    if (!validateForm()) {
      trackConversionEvent('form_error', {
        reason: 'validation',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      if (reCaptchaSiteKey && !recaptchaToken) {
        throw new Error('Captcha verification failed. Please retry.');
      }
      let submittedDate = formData.date;
      let submittedTime = formData.time;
      let finalResponse: Response | null = null;

      if (isWomenSite) {
        const maxAttempts = 12;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const slot = getWomenAutoSlot(attempt);
          submittedDate = slot.date;
          submittedTime = slot.time;

          const requestPayload = {
            ...formData,
            servicePricing: JSON.stringify(servicePricing),
            ...attribution,
            ...getAdsContext(),
            email: '',
            date: submittedDate,
            time: submittedTime,
            ...(reCaptchaSiteKey ? { captchaToken: recaptchaToken } : {}),
          };

          const response = await fetch(`${apiBaseUrl}/api/appointments`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestPayload),
          });

          if (response.ok) {
            finalResponse = response;
            break;
          }

          const payload = await response.json().catch(() => null);
          const isSlotConflict = payload?.code === 'SLOT_CONFLICT' || String(payload?.message || '').includes('slot');

          if (!isSlotConflict) {
            throw new Error(payload?.message || t.errorMessage);
          }
        }
      } else {
        const requestPayload = {
          ...formData,
          servicePricing: JSON.stringify(servicePricing),
          ...attribution,
          ...getAdsContext(),
          date: submittedDate,
          time: submittedTime,
          ...(reCaptchaSiteKey ? { captchaToken: recaptchaToken } : {}),
        };

        const response = await fetch(`${apiBaseUrl}/api/appointments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
        });

        if (response.ok) {
          finalResponse = response;
        } else {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.message || t.errorMessage);
        }
      }

      if (!finalResponse) {
        throw new Error(t.errorMessage);
      }

      if (!finalResponse?.ok) {
        throw new Error(t.errorMessage);
      }

      trackConversionEvent('form_success', {
        service: formData.service,
        city: formData.city,
        auto_slot: isWomenSite ? '1' : '0',
      });

      setIsSubmitting(false);

      const params = new URLSearchParams({
        date: submittedDate,
        time: submittedTime,
        service: formData.service,
        city: formData.city,
      });

      setLocation(`/thank-you?${params.toString()}`);
    } catch (error) {
      trackConversionEvent('form_error', {
        reason: 'api',
        message: error instanceof Error ? error.message : 'unknown',
      });
      setApiError(error instanceof Error ? error.message : t.errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleSelectDate = (date: string) => {
    updateFormField('date', date);
    setTouched(prev => ({ ...prev, date: true }));
    setCalendarRefreshKey((previous) => previous + 1);
  };

  const handleSelectTime = (time: string) => {
    updateFormField('time', time);
    setTouched(prev => ({ ...prev, time: true }));
  };

  return (
    <>
      <div className="relative left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-2xl overflow-hidden w-[calc(100%+0.75rem)] max-w-md sm:static sm:translate-x-0 sm:w-full sm:max-w-5xl sm:mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-black to-gray-900 text-white p-6 text-center">
          <h2 className="text-2xl font-bold mb-2">{t.title}</h2>
          <p className="text-xs leading-5 text-gray-300 sm:text-sm sm:leading-6">
            {language === 'fr' ? (
              <>
                <span className="block sm:inline">Choisissez votre soin, votre centre et votre créneau </span>
                <span className="block sm:inline">puis renseignez vos coordonnées.</span>
              </>
            ) : t.subtitle}
          </p>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          {apiError && (
            <p className="text-red-600 text-sm text-center font-medium lg:col-span-2">{apiError}</p>
          )}

          {/* Full Name */}
          <div className={`order-5 ${formStep === 1 ? 'hidden' : ''}`}>
            <label className="block text-sm font-semibold text-black mb-2">
              <User size={16} className="inline mr-2 text-[#D4AF37]" />
              {t.fullName} *
            </label>
            <Input
              type="text"
              name="fullName"
              autoComplete="name"
              placeholder={t.fullName}
              value={formData.fullName}
              onChange={(e) => updateFormField('fullName', e.target.value)}
              onBlur={() => {
                syncBrowserFilledFields();
                setTouched(prev => ({ ...prev, fullName: true }));
              }}
              className={`border-2 ${errors.fullName && touched.fullName ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 text-black`}
            />
            {errors.fullName && touched.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>

          {!isWomenSite && (
            <div className={`order-6 ${formStep === 1 ? 'hidden' : ''}`}>
              <label className="block text-sm font-semibold text-black mb-2">
                <Mail size={16} className="inline mr-2 text-[#D4AF37]" />
                {t.email}
              </label>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder={t.email}
                value={formData.email}
                onChange={(e) => updateFormField('email', e.target.value)}
                onBlur={() => {
                  syncBrowserFilledFields();
                  setTouched(prev => ({ ...prev, email: true }));
                }}
                className={`border-2 ${errors.email && touched.email ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 text-black`}
              />
              {errors.email && touched.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          )}

          {/* Phone */}
          <div className={`order-7 ${formStep === 1 ? 'hidden' : ''}`}>
            <label className="block text-sm font-semibold text-black mb-2">
              <Phone size={16} className="inline mr-2 text-[#D4AF37]" />
              {t.phone} *
            </label>
            <Input
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={t.phone}
              value={formData.phone}
              onChange={(e) => updateFormField('phone', e.target.value.replace(/\D/g, ''))}
              onBlur={() => {
                syncBrowserFilledFields();
                setTouched(prev => ({ ...prev, phone: true }));
              }}
              className={`border-2 ${errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 text-black`}
            />
            {errors.phone && touched.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Service */}
          <div className={`order-1 ${formStep === 2 ? 'hidden' : ''}`}>
            <label htmlFor="appointment-service" className="block text-sm font-semibold text-black mb-2">
              <span className="text-[#D4AF37]">✨</span> {t.service} *
            </label>
            <select
              id="appointment-service"
              name="service"
              aria-label={t.service}
              className="w-full border-2 border-gray-300 rounded-lg h-12 bg-white px-3 text-base text-black"
              value={formData.service}
              onChange={(event) => {
                const value = event.target.value;
                updateFormField('service', value);
                const pricingOptions = SERVICE_PRICING_OPTIONS[value as ServiceKey] || [];
                setServicePricing(pricingOptions[0] ? [pricingOptions[0].value] : []);
              }}
            >
              <option value="" disabled>{t.service}</option>
              {SERVICE_KEYS.map((serviceKey) => (
                <option key={serviceKey} value={serviceKey}>
                  {getServiceLabel(serviceKey, language)}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          {currentPricingOptions.length > 0 && (
            <fieldset className={`order-1 rounded-lg border border-gray-300 p-4 ${formStep === 2 ? 'hidden' : ''}`}>
              <legend className="px-1 text-sm font-semibold text-black">Tarifs *</legend>
              <div className="space-y-3">
                {currentPricingOptions.map((option) => (
                  <label key={option.value} className="flex items-start gap-3 text-sm text-black">
                    <input
                      type="radio"
                      name="servicePricing"
                      checked={servicePricing.includes(option.value)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setServicePricing([option.value]);
                        }
                      }}
                      className="mt-0.5 h-4 w-4 accent-[#D4AF37]"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.servicePricing && touched.servicePricing && (
                <p className="text-red-500 text-sm mt-2">{errors.servicePricing}</p>
              )}
            </fieldset>
          )}

          {/* City */}
          <div className={`order-2 ${formStep === 2 ? 'hidden' : ''}`}>
            <label htmlFor="appointment-city" className="block text-sm font-semibold text-black mb-2">
              <MapPin size={16} className="inline mr-2 text-[#D4AF37]" />
              {t.city} *
            </label>
            <select
              id="appointment-city"
              name="city"
              aria-label={t.city}
              className="w-full border-2 border-gray-300 rounded-lg h-12 bg-white px-3 text-base text-black"
              value={formData.city}
              onChange={(event) => updateFormField('city', event.target.value)}
            >
              <option value="" disabled>{t.city}</option>
              <option value="casablanca">{t.cities.casablanca}</option>
              <option value="rabat">{t.cities.rabat}</option>
            </select>
          </div>

          {!isWomenSite && (
            <>
              <div className={`order-3 ${formStep === 2 ? 'hidden' : ''}`}>
                <label className="block text-sm font-semibold text-black mb-2">
                  <Calendar size={16} className="inline mr-2 text-[#D4AF37]" />
                  {t.date} *
                </label>
                <button
                  type="button"
                  onClick={() => setIsCalendarOpen(true)}
                  disabled={!formData.city}
                  className="w-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 text-black rounded-lg px-4 py-2 font-semibold hover:bg-[#D4AF37]/20 transition hover:shadow-lg hover:shadow-[#D4AF37]/50 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.city) return;
                    e.currentTarget.style.boxShadow = '0 0 20px 10px rgba(212, 175, 55, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {formData.date ? formData.date : t.openCalendar}
                </button>
                {errors.date && touched.date && (
                  <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                )}
              </div>

              <div className={`order-4 ${formStep === 2 ? 'hidden' : ''}`}>
                <label className="block text-sm font-semibold text-black mb-2">
                  <Clock size={16} className="inline mr-2 text-[#D4AF37]" />
                  {t.time} *
                </label>
                <button
                  type="button"
                  onClick={() => setIsTimeOpen(true)}
                  disabled={!formData.city || !formData.date}
                  className="w-full border-2 border-[#D4AF37] bg-[#D4AF37]/10 text-black rounded-lg px-4 py-2 font-semibold hover:bg-[#D4AF37]/20 transition hover:shadow-lg hover:shadow-[#D4AF37]/50 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.city || !formData.date) return;
                    e.currentTarget.style.boxShadow = '0 0 20px 10px rgba(212, 175, 55, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {formData.time ? formData.time : t.openTimes}
                </button>
                {errors.time && touched.time && (
                  <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                )}
              </div>
            </>
          )}

          {formStep === 1 ? (
            <Button
              type="button"
              onClick={handleFirstStep}
              className="order-5 flex w-full items-center justify-center gap-2 bg-black py-3 font-bold text-white transition hover:bg-gray-900 lg:col-span-2"
            >
              {t.continue}
              <ArrowRight size={18} aria-hidden="true" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => setFormStep(1)}
              className="order-8 flex w-full items-center justify-center gap-2 border-2 border-black bg-white py-3 font-bold text-black transition hover:bg-gray-100 lg:col-span-2"
            >
              <ArrowLeft size={18} aria-hidden="true" />
              {t.back}
            </Button>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            style={{
              order: 9,
              transition: 'all 0.3s ease',
            }}
            disabled={
              isSubmitting ||
              !formData.fullName.trim() ||
              !formData.phone.trim() ||
              !formData.service.trim() ||
              !formData.city.trim() ||
              (currentPricingOptions.length > 0 && servicePricing.length === 0) ||
              (!isWomenSite && (!formData.date.trim() || !formData.time.trim()))
            }
            className={`w-full bg-black hover:bg-gray-900 text-white font-bold py-3 rounded-lg transition mt-6 hover:shadow-lg hover:shadow-[#D4AF37]/50 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed lg:col-span-2 ${formStep === 1 ? 'hidden' : ''}`}
            onMouseEnter={(e) => {
              if (!isSubmitting && formData.fullName.trim() && formData.phone.trim() && formData.service.trim() && formData.city.trim() && (isWomenSite || (formData.date.trim() && formData.time.trim()))) {
                e.currentTarget.style.boxShadow = '0 0 20px 10px rgba(212, 175, 55, 0.5)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isSubmitting
              ? isWomenSite
                ? t.submittingWomen
                : t.submitting
              : isWomenSite
                ? t.submitWomen
                : t.submit}
          </Button>

          {isWomenSite && (
            <p className="text-xs text-gray-600 text-center lg:col-span-2">{t.womenQuickReply}</p>
          )}
        </form>
      </div>

      {!isWomenSite && (
        <>
          {isCalendarOpen && (
            <Suspense fallback={null}>
              <CalendarModal
                key={calendarRefreshKey}
                isOpen={isCalendarOpen}
                onClose={() => setIsCalendarOpen(false)}
                onSelectDate={handleSelectDate}
                onSelectTime={handleSelectTime}
                selectedCity={formData.city}
              />
            </Suspense>
          )}

          {isTimeOpen && (
            <Suspense fallback={null}>
              <TimeModal
                isOpen={isTimeOpen}
                onClose={() => setIsTimeOpen(false)}
                onSelectTime={handleSelectTime}
                selectedDate={formData.date}
                selectedCity={formData.city}
              />
            </Suspense>
          )}
        </>
      )}
    </>
  );
}
